import React, { useState, useEffect } from "react";
import {
  Col,
  Card,
  CardBody,
  Form,
  Input,
  Button,
  Dropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
} from "reactstrap";
import { Link } from "react-router-dom";
import toast, { Toaster } from "react-hot-toast";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchDevicesRequest,
  updatePaymentRequest,
  searchPaymentsRequest,
} from "../../store/DevicesPaymentStore/devicesActions";
import { useNavigate } from "react-router-dom";
import PageSection from '../../components/Loader/loader';

const AllPaymentInfo = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [editableRow, setEditableRow] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(30);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [selectedFilter, setSelectedFilter] = useState("All");
  const [editedPaymentDate, setEditedPaymentDate] = useState("");
  const [editedExpiryDate, setEditedExpiryDate] = useState("");
  const [editedInstallDate, setEditedInstallDate] = useState("");
  const [editedPaymentType, setEditedPaymentType] = useState("Free Trial");
  const [initialPaymentDate, setInitialPaymentDate] = useState("");
  const [initialExpiryDate, setInitialExpiryDate] = useState("");
  const [initialInstallDate, setInitialInstallDate] = useState("");
  const [initialPaymentType, setInitialPaymentType] = useState("Free Trial");
  const [fetchedEntries, setFetchedEntries] = useState([]);
  const [totalFetchedCount, setTotalFetchedCount] = useState(0);
  const [displayedItems, setDisplayedItems] = useState([]);
  const [noResultsFound, setNoResultsFound] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);

  const dispatch = useDispatch();
  const { devicesData, searchResults = [], loading } = useSelector((state) => state.devices);

  useEffect(() => {
    if (!devicesData || devicesData.length === 0) {
      dispatch(fetchDevicesRequest(1, 120));
    } else {
      setDisplayedItems(devicesData);
    }
  }, [dispatch, devicesData]);

  useEffect(() => {
    if (currentPage % 4 === 0 && totalFetchedCount <= currentPage * itemsPerPage) {
      const nextPage = currentPage / 4 + 1;
      dispatch(fetchDevicesRequest(nextPage, 120));
    }
  }, [currentPage, dispatch, totalFetchedCount]);

  // Search effect
  useEffect(() => {
    const handleSearch = () => {
      if (searchTerm.length >= 3) {
        setSearchLoading(true);
        dispatch(searchPaymentsRequest(searchTerm));
      } else {
        setDisplayedItems(devicesData);
        setNoResultsFound(false);
      }
    };

    const timeoutId = setTimeout(handleSearch, 500);

    return () => {
      clearTimeout(timeoutId);
    };
  }, [searchTerm, dispatch, devicesData]);

  useEffect(() => {
    if (searchTerm.length >= 3) {
      setSearchLoading(false);
      if (searchResults.length > 0) {
        setDisplayedItems(searchResults);
        setNoResultsFound(false);
      } else {
        setNoResultsFound(true);
        const clearItemsTimeout = setTimeout(() => {
          setDisplayedItems([]);
        }, 500);

        return () => clearTimeout(clearItemsTimeout);
      }
    } else {
      setDisplayedItems(devicesData);
      setNoResultsFound(false);
    }
  }, [searchResults, searchTerm, devicesData]);


  const handleEdit = (row) => {
    setEditableRow(row.device_ID);
    setInitialPaymentDate(
      (row.paymentdetails && row.paymentdetails[0]?.payment_date) || ""
    );
    setInitialExpiryDate(
      (row.appdetails && row.appdetails[0]?.expiry_date) || ""
    );
    setInitialInstallDate(
      (row.appdetails && row.appdetails[0]?.first_install) || ""
    );
    setInitialPaymentType(
      (row.paymentdetails && row.paymentdetails[0]?.payment_type) || "Free Trial"
    );
    setEditedPaymentDate(
      (row.paymentdetails && row.paymentdetails[0]?.payment_date) || ""
    );
    setEditedExpiryDate(
      (row.appdetails && row.appdetails[0]?.expiry_date) || ""
    );
    setEditedInstallDate(
      (row.appdetails && row.appdetails[0]?.first_install) || ""
    );
    setEditedPaymentType(
      (row.paymentdetails && row.paymentdetails[0]?.payment_type) || "Free Trial"
    );
  };

  const handleSave = (rowId) => {
    const updatedPayment = {
      device_ID: rowId,
      payment_expiry_date: editedExpiryDate,
      payment_type: editedPaymentType,
    };

    // Update the local state immediately
    setDisplayedItems((prevItems) =>
      prevItems.map((item) =>
        item.device_ID === rowId
          ? {
            ...item,
            paymentdetails: [
              {
                ...item.paymentdetails[0],
                payment_type: editedPaymentType,
              },
            ],
            appdetails: [
              {
                ...item.appdetails[0],
                expiry_date: editedExpiryDate,
              },
            ],
          }
          : item
      )
    );

    dispatch(updatePaymentRequest(updatedPayment));
    toast.success('Payment Data Update')
    setEditableRow(null);
  };

  const handleCancel = () => {
    setEditableRow(null);
    setEditedPaymentDate(initialPaymentDate);
    setEditedExpiryDate(initialExpiryDate);
    setEditedInstallDate(initialInstallDate);
    setEditedPaymentType(initialPaymentType);
  };

  const toggleDropdown = () => setDropdownOpen((prevState) => !prevState);

  const handleFilterChange = (filter) => {
    setSelectedFilter(filter);

    if (filter === "All") {
      setSearchTerm("");
      setDisplayedItems(devicesData);
    } else {
      setSearchTerm(filter);
    }
  };

  const totalPages = Math.ceil(displayedItems.length / itemsPerPage);
  const indexOfFirstItem = (currentPage - 1) * itemsPerPage;
  const displayedPaginationItems = displayedItems.slice(indexOfFirstItem, indexOfFirstItem + itemsPerPage);

  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  const renderPageNumbers = () => {
    const pageNumbers = [];
    let startPage, endPage;

    if (totalPages <= 3) {
      startPage = 1;
      endPage = totalPages;
    } else {
      if (currentPage <= 2) {
        startPage = 1;
        endPage = 3;
      } else if (currentPage + 1 >= totalPages) {
        startPage = totalPages - 2;
        endPage = totalPages;
      } else {
        startPage = currentPage - 1;
        endPage = currentPage + 1;
      }
    }

    for (let i = startPage; i <= endPage; i++) {
      pageNumbers.push(
        <li
          key={i}
          className={`page-item ${i === currentPage ? "active" : ""}`}
        >
          <Link to="#" className="page-link" onClick={() => paginate(i)}>
            {i}
          </Link>
        </li>
      );
    }

    return pageNumbers;
  };

  const currentDate = new Date();
  const calculateRemainingTime = (expiry_date) => {
    const expiryDate = new Date(expiry_date);

    if (expiryDate < currentDate) {
      return "0";
    }

    let diffYears = expiryDate.getFullYear() - currentDate.getFullYear();
    let diffMonths = expiryDate.getMonth() - currentDate.getMonth();
    let diffDays = expiryDate.getDate() - currentDate.getDate();

    if (diffDays < 0) {
      diffMonths--;
      diffDays += new Date(
        currentDate.getFullYear(),
        currentDate.getMonth() + 1,
        0
      ).getDate();
    }

    if (diffMonths < 0) {
      diffYears--;
      diffMonths += 12;
    }

    return `${diffYears ? diffYears + " year " : ""}${diffMonths ? diffMonths + " month " : ""
      }${diffDays ? diffDays + " days" : ""}`;
  };

  const getStatusColor = (paymentType, expiry_date) => {
    if (paymentType === "Free Trial") {
      if (expiry_date) {
        const expiryDate = new Date(expiry_date);
        return expiryDate < currentDate ? "red" : "#3e94e4";
      }
      return "#3e94e4";
    }

    if (expiry_date) {
      const expiryDate = new Date(expiry_date);
      return expiryDate >= currentDate ? "green" : "red";
    }

    return "gray";
  };



  return (
    <React.Fragment>
      <Toaster />
      <Col lg={12}>
        <Card style={{ paddingBottom: "55px" }}>
          <CardBody>
            <div className="d-flex justify-content-between align-items-center mb-1">
              <div className="d-flex justify-content-between align-items-center">
                <h4 className="card-title mt-1">Payment</h4>
                <div
                  style={{
                    width: "11px",
                    height: "11px",
                    borderRadius: "50%",
                    display: "inline-block",
                    backgroundColor: "green",
                    marginLeft: "50px",
                  }}
                ></div>
                <span
                  className="ms-2"
                  style={{ fontSize: "15px", fontWeight: "400" }}
                >
                  Active
                </span>
                <div
                  style={{
                    width: "11px",
                    height: "11px",
                    borderRadius: "50%",
                    display: "inline-block",
                    backgroundColor: "red",
                    marginLeft: "20px",
                  }}
                ></div>
                <span
                  className="ms-2"
                  style={{ fontSize: "15px", fontWeight: "400" }}
                >
                  Expires
                </span>

                <div
                  style={{
                    width: "11px",
                    height: "11px",
                    borderRadius: "50%",
                    display: "inline-block",
                    backgroundColor: "#3e94e4",
                    marginLeft: "20px",
                  }}
                ></div>
                <span
                  className="ms-2"
                  style={{ fontSize: "15px", fontWeight: "400" }}
                >
                  Free Trial
                </span>
              </div>
              <div className="d-flex align-items-center">
                <Form className="app-search d-none d-lg-block pt-2" onSubmit={(e) => e.preventDefault()}>
                  <div className="position-relative d-flex align-items-center">
                    <Input
                      type="text"
                      className="form-control"
                      style={{ width: "55%" }}
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      placeholder="Search"
                    />
                    <span className="ri-search-line"></span>
                    <Dropdown
                      isOpen={dropdownOpen}
                      toggle={toggleDropdown}
                      className="ms-2"
                    >
                      <DropdownToggle
                        style={{ fontSize: "13px", padding: "7px 9px" }}
                        caret
                      >
                        {selectedFilter}
                      </DropdownToggle>
                      <DropdownMenu>
                        <DropdownItem onClick={() => handleFilterChange("All")}>
                          All
                        </DropdownItem>
                        <DropdownItem
                          onClick={() => handleFilterChange("Lifetime")}
                        >
                          Lifetime
                        </DropdownItem>
                        <DropdownItem
                          onClick={() => handleFilterChange("Monthly")}
                        >
                          Monthly
                        </DropdownItem>
                        <DropdownItem
                          onClick={() => handleFilterChange("Yearly")}
                        >
                          Yearly
                        </DropdownItem>
                        <DropdownItem
                          onClick={() => handleFilterChange("Free Trial")}
                        >
                          Free Trial
                        </DropdownItem>
                      </DropdownMenu>
                    </Dropdown>
                    <Link to="/view_lifeTime_email">
                      <Button
                        style={{ fontSize: "13px", padding: "7px 9px" }}
                        color="primary"
                        className="ms-2"
                      >
                        Beta Emails
                      </Button>
                    </Link>
                  </div>
                </Form>
              </div>
            </div>
            {loading || searchLoading ? (
              <div className="table-responsive p-2" style={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                height: "50%"
              }}>
                <PageSection />
              </div>
            ) : (
              <div className="table-responsive">
                <table
                  className="table table-bordered"
                  style={{ minWidth: "max-content" }}
                >
                  <thead>
                    <tr className="align-self-center">
                      <th className="p-3">No.</th>
                      <th className="text-left">Payment Type</th>
                      <th className="text-left">Device ID</th>
                      <th className="text-left">Email</th>
                      <th className="text-left">Install Date</th>
                      <th className="text-left">Payment Date</th>
                      <th className="text-left">Expiry Date</th>
                      <th className="text-left">Remaining Time</th>
                      <th className="text-left">Status</th>
                      <th className="text-left">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {displayedPaginationItems.length === 0 ? (
                      <tr>
                        <td colSpan="12" className="text-center p-5">
                          {noResultsFound ? (
                            <div>No data found</div>
                          ) : (
                            <PageSection />
                          )}
                        </td>
                      </tr>
                    ) : (
                      displayedPaginationItems.map((item, index) => (
                        <tr key={index} className="align-self-center">
                          <td className="p-3">{indexOfFirstItem + index + 1}</td>
                          <td className="p-3">
                            {editableRow === item.device_ID ? (
                              <Input
                                type="select"
                                value={editedPaymentType}
                                onChange={(e) =>
                                  setEditedPaymentType(e.target.value)
                                }
                              >
                                <option value="Free Trial">Free Trial</option>
                                <option value="Lifetime">Lifetime</option>
                                <option value="Monthly">Monthly</option>
                                <option value="Yearly">Yearly</option>
                              </Input>
                            ) : item.paymentdetails &&
                              item.paymentdetails[0]?.payment_type ? (
                              item.paymentdetails[0].payment_type
                            ) : (
                              "Free Trial"
                            )}
                          </td>
                          <td className="p-3">{item.device_ID}</td>
                          <td className="p-3">
                            {item.lifesubscriptions[0]?.email || ""}
                          </td>
                          <td className="p-3">
                            {item.appdetails && item.appdetails[0]?.first_install
                              ? item.appdetails[0].first_install.split("T")[0]
                              : ""}
                          </td>
                          <td className="p-3">
                            {editableRow === item.device_ID ? (
                              <Input
                                type="date"
                                value={editedPaymentDate}
                                onChange={(e) =>
                                  setEditedPaymentDate(e.target.value)
                                }
                              />
                            ) : item.paymentdetails &&
                              item.paymentdetails[0]?.payment_date ? (
                              item.paymentdetails[0].payment_date.split("T")[0]
                            ) : (
                              ""
                            )}
                          </td>

                          <td className="p-3">
                            {editableRow === item.device_ID ? (
                              <Input
                                type="date"
                                value={editedExpiryDate}
                                onChange={(e) =>
                                  setEditedExpiryDate(e.target.value)
                                }
                              />
                            ) : item.appdetails &&
                              item.appdetails[0]?.expiry_date ? (
                              item.appdetails[0].expiry_date.split("T")[0]
                            ) : (
                              ""
                            )}
                          </td>
                          <td className="p-3">
                            {item.appdetails && item.appdetails[0]?.expiry_date
                              ? calculateRemainingTime(
                                item.appdetails[0].expiry_date
                              )
                              : ""}
                          </td>
                          <td className="p-3">
                            <div
                              style={{
                                width: "10px",
                                height: "10px",
                                borderRadius: "50%",
                                display: "inline-block",
                                backgroundColor: getStatusColor(
                                  item.paymentdetails &&
                                  item.paymentdetails[0]?.payment_type,
                                  item.appdetails &&
                                  item.appdetails[0]?.expiry_date
                                ),
                              }}
                            ></div>
                          </td>
                          <td className="p-3">
                            {editableRow === item.device_ID ? (
                              <>
                                <Link
                                  to="#"
                                  className="me-3 text-success"
                                  onClick={() => handleSave(item.device_ID)}
                                >
                                  <i className="mdi mdi-check font-size-18"></i>
                                </Link>
                                <Link
                                  to="#"
                                  className="text-danger"
                                  onClick={handleCancel}
                                >
                                  <i className="mdi mdi-close font-size-18"></i>
                                </Link>
                              </>
                            ) : (
                              <Link
                                to="#"
                                className="me-3 text-primary"
                                onClick={() => handleEdit(item)}
                              >
                                <i className="mdi mdi-pencil font-size-18"></i>
                              </Link>
                            )}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
                <nav>
                  <ul
                    className="pagination justify-content-end"
                    style={{
                      position: "absolute",
                      marginTop: "15px",
                      right: "20px",
                    }}
                  >
                    <li
                      className={`page-item ${currentPage === 1 ? "disabled" : ""
                        }`}
                    >
                      <Link
                        to="#"
                        className="page-link"
                        onClick={() => paginate(currentPage - 1)}
                      >
                        &laquo;
                      </Link>
                    </li>
                    {renderPageNumbers()}
                    <li
                      className={`page-item ${currentPage === totalPages ? "disabled" : ""
                        }`}
                    >
                      <Link
                        to="#"
                        className="page-link"
                        onClick={() => paginate(currentPage + 1)}
                      >
                        &raquo;
                      </Link>
                    </li>
                  </ul>
                </nav>
              </div>
            )}
          </CardBody>
        </Card>
      </Col>
    </React.Fragment>
  );
};

export default AllPaymentInfo;
